/**
 * DB Sync Service
 * Loads all business data from Supabase into localStorage
 * so the existing localStorage-based services work with real DB data.
 * Also sets up realtime subscriptions for live updates.
 */
import { supabase } from '@/integrations/supabase/client';
import type { Contact, Conversation, Message } from '@/types';
import { getCurrentWorkspaceId } from './workspaceState';

// ─── Field mappers (DB snake_case → frontend camelCase) ───

function normalizePhoneIdentity(phone: string): string {
  return String(phone || '').replace(/\D/g, '');
}

const AI_SETTINGS_STORAGE_KEY = 'wf_ai_settings';

function getAISettingsStorageKey(workspaceId: string): string {
  return `${AI_SETTINGS_STORAGE_KEY}:${workspaceId || 'default'}`;
}

function normalizeAIProvider(provider?: string | null): string {
  const value = String(provider || '').trim();
  if (!value) return 'Lovable AI (Recommended)';

  const lower = value.toLowerCase();
  if (lower === 'lovable' || lower.includes('gemini')) return 'Lovable AI (Recommended)';
  if (lower.includes('openai') || lower.includes('gpt')) return 'OpenAI GPT-5';
  if (lower.includes('claude')) return 'Claude 3.5';
  return value;
}

function normalizeAIModel(model?: string | null): string {
  const value = String(model || '').trim();
  if (!value) return 'google/gemini-3-flash-preview';

  const aliases: Record<string, string> = {
    'gemini-3-flash-preview': 'google/gemini-3-flash-preview',
    'gemini-3.1-pro-preview': 'google/gemini-3.1-pro-preview',
    'gemini-2.5-pro': 'google/gemini-2.5-pro',
    'gemini-2.5-flash': 'google/gemini-2.5-flash',
    'gpt-5': 'openai/gpt-5',
    'gpt-5-mini': 'openai/gpt-5-mini',
  };

  return aliases[value] || value;
}

function mapContact(row: any): Contact {
  return {
    id: row.id,
    fullName: row.full_name,
    phoneNumber: normalizePhoneIdentity(row.phone_number),
    email: row.email || undefined,
    city: row.city || undefined,
    country: row.country || 'Morocco',
    language: row.language || 'Arabic',
    tags: row.tags || [],
    leadStatus: row.lead_status || 'new',
    assignedAgentId: row.assigned_agent_id || undefined,
    source: row.source || 'WhatsApp',
    notesCount: row.notes_count || 0,
    lastContactAt: row.last_contact_at || undefined,
    lastMessageAt: row.last_message_at || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    productName: row.product_name || undefined,
    orderAmount: row.order_amount || undefined,
    orderStatus: row.order_status || undefined,
    deliveryStatus: row.delivery_status || undefined,
    codConfirmationStatus: row.cod_confirmation_status || undefined,
    currency: row.currency || undefined,
    orderCreatedAt: row.order_created_at || undefined,
    lastOrderUpdateAt: row.last_order_update_at || undefined,
  };
}

function mapConversation(row: any): Conversation {
  return {
    id: row.id,
    contactId: row.contact_id,
    channelType: row.channel_type || 'whatsapp_api',
    status: row.status || 'open',
    assignedAgentId: row.assigned_agent_id || undefined,
    unreadCount: row.unread_count || 0,
    tags: row.tags || [],
    lastMessagePreview: row.last_message_preview || undefined,
    lastMessageAt: row.last_message_at || undefined,
    pinned: !!row.pinned,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapMessage(row: any): Message {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    contactId: row.contact_id,
    direction: row.direction,
    messageType: row.message_type || 'text',
    body: row.body || '',
    deliveryState: row.delivery_state || 'delivered',
    sentBy: row.sent_by || undefined,
    templateId: row.template_id || undefined,
    campaignId: row.campaign_id || undefined,
    automationId: row.automation_id || undefined,
    externalMessageId: row.external_message_id || undefined,
    mediaUrl: row.media_url || undefined,
    transcriptionText: row.transcription_text || undefined,
    transcriptionStatus: row.transcription_status || undefined,
    transcriptionError: row.transcription_error || undefined,
    transcriptionLanguage: row.transcription_language || undefined,
    contextWamid: row.context_wamid || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── localStorage helpers ───
import { _notifyKey, safeSetItem } from './dataService';

function setCollection<T>(key: string, data: T[]): void {
  safeSetItem(key, JSON.stringify(data));
}

function getCollection<T>(key: string): T[] {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

function setDocument<T>(key: string, data: T): void {
  safeSetItem(key, JSON.stringify(data));
}

let _synced = false;
let _realtimeChannel: any = null;

/** Clear all workspace-specific localStorage data (keeps UI prefs like theme, sidebar state) */
function clearWorkspaceLocalStorage() {
  const uiPrefixes = ['wf_ui_', 'wf_current_workspace', 'wf_admin_workspace', 'wf_schema_version'];
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('wf_') && !uiPrefixes.some(p => key.startsWith(p))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
}

/** Reset sync state so next call to syncFromDb does a fresh load (call on login/logout) */
export function resetDbSync() {
  _synced = false;
  clearWorkspaceLocalStorage();
  if (_realtimeChannel) {
    supabase.removeChannel(_realtimeChannel);
    _realtimeChannel = null;
  }
}

/**
 * Load ALL business data from DB into localStorage.
 * Called once on app startup after login.
 */
export async function syncFromDb(): Promise<void> {
  const wsId = getCurrentWorkspaceId();
  if (!wsId || wsId === 'default') {
    console.warn('[dbSync] Aborting sync — workspace_id not resolved yet (got:', wsId, ')');
    return;
  }
  // Helper: paginate through all rows (Supabase caps responses at 1000 by default)
  const fetchAllPaged = async <T = any>(
    table: string,
    orderColumn: string,
    ascending: boolean
  ): Promise<T[]> => {
    const PAGE = 1000;
    const all: T[] = [];
    let from = 0;
    // Safety cap to avoid runaway loops (50k rows max per table)
    while (from < 50000) {
      const { data, error } = await supabase
        .from(table as any)
        .select('*')
        .eq('workspace_id', wsId)
        .order(orderColumn, { ascending })
        .range(from, from + PAGE - 1);
      if (error || !data || data.length === 0) break;
      all.push(...(data as T[]));
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return all;
  };

  try {
    // 1. Contacts (paginated)
    const contactRows = await fetchAllPaged('contacts', 'created_at', false);
    if (contactRows.length) {
      const contacts = contactRows.map(mapContact);
      setCollection('wf_contacts', contacts);
      _notifyKey('wf_contacts');
    }

    // 2. Conversations (paginated — fetch ALL, no 1000 cap)
    const convRows = await fetchAllPaged('conversations', 'last_message_at', false);
    if (convRows.length) {
      const conversations = convRows.map(mapConversation);
      setCollection('wf_conversations', conversations);
      _notifyKey('wf_conversations');
    }

    // 3. Messages: NOT pre-fetched anymore.
    //    Each conversation's messages are loaded lazily when the user opens it
    //    (see Inbox.tsx → loadOlderMessages). This dramatically reduces initial
    //    Cloud usage: previously we ran 1 query per conversation × 50 messages
    //    on every page load, even for chats the user never opened.

    // 4. Templates
    const { data: templateRows } = await supabase
      .from('templates')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false });

    if (templateRows) {
      const templates = templateRows.map((r: any) => ({
        id: r.id,
        name: r.name,
        category: r.category,
        language: r.language,
        headerType: r.header_type,
        headerText: r.header_text || undefined,
        headerMediaUrl: r.header_media_url || undefined,
        bodyText: r.body_text,
        footerText: r.footer_text || undefined,
        buttons: r.buttons || [],
        variables: r.variables || [],
        status: r.status,
        metaTemplateId: r.meta_template_id || undefined,
        metaTemplateName: r.meta_template_name || undefined,
        previewSampleData: r.preview_sample_data || undefined,
        qualityScore: r.quality_score || undefined,
        rejectionReason: r.rejection_reason || undefined,
        submittedAt: r.submitted_at || undefined,
        usageCount: r.usage_count || 0,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setCollection('wf_templates', templates);
      _notifyKey('wf_templates');
    }

    // 5. System logs (recent 200)
    const { data: logRows } = await supabase
      .from('system_logs')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false })
      .limit(200);

    if (logRows) {
      const logs = logRows.map((r: any) => ({
        id: r.id,
        module: r.module,
        action: r.action,
        entityType: r.entity_type || undefined,
        entityId: r.entity_id || undefined,
        level: r.level,
        message: r.message,
        metadata: r.metadata || {},
        createdAt: r.created_at,
      }));
      setCollection('wf_logs', logs);
      _notifyKey('wf_logs');
    }

    // 6. Campaigns
    const { data: campaignRows } = await supabase
      .from('campaigns')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false });

    if (campaignRows) {
      const campaigns = campaignRows.map((r: any) => ({
        id: r.id,
        name: r.name,
        type: r.type,
        status: r.status,
        templateId: r.template_id || undefined,
        audienceFilters: r.audience_filters || undefined,
        audienceCount: r.audience_count || undefined,
        scheduledAt: r.scheduled_at || undefined,
        startedAt: r.started_at || undefined,
        finishedAt: r.finished_at || undefined,
        totalRecipients: r.total_recipients || 0,
        sentCount: r.sent_count || 0,
        deliveredCount: r.delivered_count || 0,
        readCount: (r as any).read_count || 0,
        failedCount: r.failed_count || 0,
        repliedCount: r.replied_count || 0,
        errorReason: r.error_reason || undefined,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setCollection('wf_campaigns', campaigns);
      _notifyKey('wf_campaigns');

      // Load recipients for each campaign
      for (const camp of campaignRows) {
        const { data: recipientRows } = await supabase
          .from('campaign_recipients')
          .select('*')
          .eq('campaign_id', camp.id);

        if (recipientRows && recipientRows.length > 0) {
          const recipients = recipientRows.map((r: any) => ({
            id: r.id,
            campaignId: r.campaign_id,
            contactId: r.contact_id,
            conversationId: r.conversation_id || undefined,
            status: r.status,
            errorReason: r.error_reason || undefined,
            sentAt: r.sent_at || undefined,
            deliveredAt: r.delivered_at || undefined,
            readAt: (r as any).read_at || undefined,
            repliedAt: r.replied_at || undefined,
            createdAt: r.created_at,
          }));
          setCollection(`wf_campaign_recipients_${camp.id}`, recipients);
          _notifyKey(`wf_campaign_recipients_${camp.id}`);
        }
      }
    }

    // 7. Notifications (recent 200)
    const { data: notifRows } = await supabase
      .from('notifications')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false })
      .limit(200);

    if (notifRows) {
      const notifications = notifRows.map((r: any) => ({
        id: r.id,
        type: r.type,
        title: r.title,
        message: r.message,
        module: r.module,
        relatedId: r.related_id || undefined,
        isRead: r.is_read,
        createdAt: r.created_at,
      }));
      setCollection('wf_notifications', notifications);
      _notifyKey('wf_notifications');
    }

    // 8. AI Settings
    const [{ data: aiSettingsRows }, { data: integrationsRow }] = await Promise.all([
      supabase
        .from('ai_settings')
        .select('*')
        .eq('workspace_id', wsId)
        .order('updated_at', { ascending: false })
        .limit(1),
      supabase
        .from('integrations_config')
        .select('ai_config')
        .eq('workspace_id', wsId)
        .maybeSingle(),
    ]);

    const aiSettingsRow = Array.isArray(aiSettingsRows) ? aiSettingsRows[0] : aiSettingsRows;
    const aiConfig = ((integrationsRow as any)?.ai_config || {}) as Record<string, any>;
    if (aiSettingsRow || aiConfig.apiKey) {
      const s = (aiSettingsRow as any) || {};
      const rawFeatures = s.features && typeof s.features === 'object'
        ? { ...(s.features as Record<string, any>) }
        : {};
      const nestedVoiceConfig =
        rawFeatures.voiceConfig && typeof rawFeatures.voiceConfig === 'object'
          ? { ...(rawFeatures.voiceConfig as Record<string, any>) }
          : {};
      delete rawFeatures.voiceConfig;

      const aiSettings = {
        id: 'ai_config',
        provider: normalizeAIProvider(s.provider || 'Lovable AI (Recommended)'),
        model: normalizeAIModel(s.model || 'google/gemini-3-flash-preview'),
        apiKey: typeof aiConfig.apiKey === 'string' ? aiConfig.apiKey : '',
        apiMode: s.api_mode || 'external',
        systemPrompt: s.system_prompt || '',
        brandTone: s.brand_tone || 'friendly',
        languageRules: s.language_rules || '',
        forbiddenWords: s.forbidden_words || [],
        productContext: s.product_context || '',
        temperature: s.temperature ?? 0.7,
        maxTokens: s.max_tokens ?? 512,
        maxResponseLines: s.max_response_lines ?? 5,
        confidenceThreshold: s.confidence_threshold ?? 0.85,
        escalationRules: s.escalation_rules || [],
        features: rawFeatures,
        voiceConfig: nestedVoiceConfig,
        updatedAt: s.updated_at,
      };
      setDocument(getAISettingsStorageKey(wsId), aiSettings);
      localStorage.removeItem(AI_SETTINGS_STORAGE_KEY);
      _notifyKey('wf_ai_settings');
    }

    // 9. Workspace Settings
    const { data: wsRows } = await supabase
      .from('workspace_settings')
      .select('*')
      .eq('workspace_id', wsId)
      .order('updated_at', { ascending: false })
      .limit(1);

    const wsRow = Array.isArray(wsRows) ? wsRows[0] : wsRows;
    if (wsRow) {
      const w = wsRow as any;
      const wsSettings = {
        id: w.id,
        workspaceId: w.workspace_id || wsId,
        general: w.general || {},
        preferences: w.preferences || {},
        createdAt: w.created_at,
        updatedAt: w.updated_at,
      };
      setDocument('wf_settings_workspace', wsSettings);
      _notifyKey('wf_settings_workspace');
    }

    // 10. Saved Replies
    const { data: replyRows } = await supabase
      .from('saved_replies')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false });

    if (replyRows) {
      const replies = replyRows.map((r: any) => ({
        id: r.id,
        title: r.title,
        body: r.body,
        category: r.category,
        usageCount: r.usage_count || 0,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setCollection('wf_saved_replies', replies);
      _notifyKey('wf_saved_replies');
    }

    // 11. Blacklist
    const { data: blacklistRows } = await supabase
      .from('blacklist')
      .select('*')
      .eq('workspace_id', wsId)
      .order('blocked_at', { ascending: false });

    if (blacklistRows) {
      const entries = blacklistRows.map((r: any) => ({
        id: r.id,
        phoneNumber: r.phone_number,
        reason: r.reason || undefined,
        blockedAt: r.blocked_at,
        createdAt: r.created_at,
      }));
      setCollection('wf_blacklist', entries);
      _notifyKey('wf_blacklist');
    }

    // 12. Contact Notes — group by contactId into per-contact keys
    const { data: noteRows } = await supabase
      .from('contact_notes')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: false })
      .limit(1000);

    if (noteRows) {
      // Group by contact_id
      const grouped: Record<string, any[]> = {};
      noteRows.forEach((r: any) => {
        const cid = r.contact_id;
        if (!grouped[cid]) grouped[cid] = [];
        grouped[cid].push({
          id: r.id,
          contactId: r.contact_id,
          body: r.body,
          createdBy: r.created_by || undefined,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        });
      });
      // Store per-contact
      for (const [contactId, notes] of Object.entries(grouped)) {
        setCollection(`wf_notes_${contactId}`, notes);
        _notifyKey(`wf_notes_${contactId}`);
      }
    }

    // 13. AI Memory
    const { data: memoryRows } = await supabase
      .from('ai_memory')
      .select('*')
      .eq('workspace_id', wsId)
      .order('updated_at', { ascending: false })
      .limit(1000);

    if (memoryRows) {
      const memories = memoryRows.map((r: any) => ({
        id: r.id,
        contactId: r.contact_id,
        conversationId: r.conversation_id || undefined,
        summary: r.summary || '',
        lastIntent: r.last_intent || 'unknown',
        lastSentiment: r.last_sentiment || 'neutral',
        leadScore: r.lead_score || 0,
        leadCategory: r.lead_category || undefined,
        interactionCount: r.interaction_count || 0,
        preferredLanguage: r.preferred_language || undefined,
        keyFacts: r.key_facts || [],
        lastAIReply: r.last_ai_reply || undefined,
        lastTemplateUsed: r.last_template_used || undefined,
        orderContext: r.order_context || undefined,
        lastContactDate: r.last_contact_date || undefined,
        lastMessageDate: r.last_message_date || undefined,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setCollection('wf_ai_memory', memories);
      _notifyKey('wf_ai_memory');
    }

    // 14. Team Members
    const { data: teamRows } = await supabase
      .from('team_members')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: true });

    if (teamRows) {
      const members = (teamRows as any[]).map((r: any) => ({
        id: r.id,
        name: r.name,
        email: r.email,
        role: r.role || 'agent',
        status: r.status || 'invited',
        isActive: r.status === 'active',
        assignedConversationsCount: 0,
        joinedAt: r.joined_at || r.created_at,
      }));
      setCollection('wf_team', members);
      _notifyKey('wf_team');
    }

    _synced = true;
    console.log('[dbSync] Full sync complete (all business data)');
  } catch (err) {
    console.error('[dbSync] Sync failed:', err);
  }
}

/**
 * Set up realtime subscriptions so new DB changes appear in UI automatically.
 */
export function setupRealtimeSync(): void {
  if (_realtimeChannel) return;

  const wsId = getCurrentWorkspaceId();
  if (!wsId || wsId === 'default') {
    console.warn('[dbSync] Skipping realtime setup — workspace_id not resolved (got:', wsId, ')');
    return;
  }
  const wsFilter = `workspace_id=eq.${wsId}`;

  _realtimeChannel = supabase
    .channel('db-sync')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts', filter: wsFilter }, (payload) => {
      handleRealtimeChange('contacts', payload);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations', filter: wsFilter }, (payload) => {
      handleRealtimeChange('conversations', payload);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'messages', filter: wsFilter }, (payload) => {
      handleRealtimeChange('messages', payload);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'campaigns', filter: wsFilter }, (payload) => {
      handleRealtimeChange('campaigns', payload);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'templates', filter: wsFilter }, (payload) => {
      handleRealtimeChange('templates', payload);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: wsFilter }, (payload) => {
      handleRealtimeChange('notifications', payload);
    })
    .subscribe();
}

function mapCampaign(row: any) {
  return {
    id: row.id, name: row.name, type: row.type, status: row.status,
    templateId: row.template_id || undefined, audienceFilters: row.audience_filters || undefined,
    audienceCount: row.audience_count || undefined, scheduledAt: row.scheduled_at || undefined,
    startedAt: row.started_at || undefined, finishedAt: row.finished_at || undefined,
    totalRecipients: row.total_recipients || 0, sentCount: row.sent_count || 0,
    deliveredCount: row.delivered_count || 0, readCount: row.read_count || 0,
    failedCount: row.failed_count || 0,
    repliedCount: row.replied_count || 0, errorReason: row.error_reason || undefined,
    createdAt: row.created_at, updatedAt: row.updated_at,
  };
}

function mapTemplate(row: any) {
  return {
    id: row.id, name: row.name, category: row.category, language: row.language,
    headerType: row.header_type, headerText: row.header_text || undefined,
    headerMediaUrl: row.header_media_url || undefined, bodyText: row.body_text,
    footerText: row.footer_text || undefined, buttons: row.buttons || [],
    variables: row.variables || [], status: row.status,
    metaTemplateId: row.meta_template_id || undefined, metaTemplateName: row.meta_template_name || undefined,
    previewSampleData: row.preview_sample_data || undefined, qualityScore: row.quality_score || undefined,
    rejectionReason: row.rejection_reason || undefined, submittedAt: row.submitted_at || undefined,
    usageCount: row.usage_count || 0, createdAt: row.created_at, updatedAt: row.updated_at,
  };
}

function mapNotification(row: any) {
  return {
    id: row.id, type: row.type, title: row.title, message: row.message,
    module: row.module, relatedId: row.related_id || undefined,
    isRead: row.is_read, createdAt: row.created_at,
  };
}

function handleRealtimeChange(table: string, payload: any) {
  const { eventType, new: newRow, old: oldRow } = payload;

  // Guard: ignore realtime events from other workspaces.
  // Server-side filter alone is insufficient because super_admin RLS allows all rows,
  // and a stale channel from a previous workspace can still deliver events.
  const currentWs = getCurrentWorkspaceId();
  const rowWs = newRow?.workspace_id || oldRow?.workspace_id;
  if (rowWs && currentWs && rowWs !== currentWs) {
    return;
  }

  if (table === 'contacts') {
    const contacts = getCollection<Contact>('wf_contacts');
    if (eventType === 'INSERT') {
      const mapped = mapContact(newRow);
      if (!contacts.find(c => c.id === mapped.id)) {
        contacts.unshift(mapped);
        setCollection('wf_contacts', contacts);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapContact(newRow);
      const idx = contacts.findIndex(c => c.id === mapped.id);
      if (idx !== -1) { contacts[idx] = mapped; }
      else { contacts.unshift(mapped); }
      setCollection('wf_contacts', contacts);
    } else if (eventType === 'DELETE') {
      setCollection('wf_contacts', contacts.filter(c => c.id !== oldRow.id));
    }
    _notifyKey('wf_contacts');
  }

  if (table === 'conversations') {
    const convs = getCollection<Conversation>('wf_conversations');
    if (eventType === 'INSERT') {
      const mapped = mapConversation(newRow);
      if (!convs.find(c => c.id === mapped.id)) {
        convs.unshift(mapped);
        setCollection('wf_conversations', convs);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapConversation(newRow);
      const idx = convs.findIndex(c => c.id === mapped.id);
      if (idx !== -1) { convs[idx] = mapped; }
      else { convs.unshift(mapped); }
      setCollection('wf_conversations', convs);
    } else if (eventType === 'DELETE') {
      setCollection('wf_conversations', convs.filter(c => c.id !== oldRow.id));
    }
    _notifyKey('wf_conversations');
  }

  if (table === 'messages') {
    const convId = newRow?.conversation_id || oldRow?.conversation_id;
    if (!convId) return;
    const key = `wf_messages_${convId}`;
    const msgs = getCollection<Message>(key);
    if (eventType === 'INSERT') {
      const mapped = mapMessage(newRow);
      if (!msgs.find(m => m.id === mapped.id)) {
        msgs.push(mapped);
        setCollection(key, msgs);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapMessage(newRow);
      const idx = msgs.findIndex(m => m.id === mapped.id);
      if (idx !== -1) { msgs[idx] = mapped; }
      else { msgs.push(mapped); }
      setCollection(key, msgs);
    }
    _notifyKey(key);
  }

  if (table === 'campaigns') {
    const campaigns = getCollection<any>('wf_campaigns');
    if (eventType === 'INSERT') {
      const mapped = mapCampaign(newRow);
      if (!campaigns.find((c: any) => c.id === mapped.id)) {
        campaigns.unshift(mapped);
        setCollection('wf_campaigns', campaigns);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapCampaign(newRow);
      const idx = campaigns.findIndex((c: any) => c.id === mapped.id);
      if (idx !== -1) { campaigns[idx] = mapped; }
      else { campaigns.unshift(mapped); }
      setCollection('wf_campaigns', campaigns);
    } else if (eventType === 'DELETE') {
      setCollection('wf_campaigns', campaigns.filter((c: any) => c.id !== oldRow.id));
    }
    _notifyKey('wf_campaigns');
  }

  if (table === 'templates') {
    const templates = getCollection<any>('wf_templates');
    if (eventType === 'INSERT') {
      const mapped = mapTemplate(newRow);
      if (!templates.find((t: any) => t.id === mapped.id)) {
        templates.unshift(mapped);
        setCollection('wf_templates', templates);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapTemplate(newRow);
      const idx = templates.findIndex((t: any) => t.id === mapped.id);
      if (idx !== -1) { templates[idx] = mapped; }
      else { templates.unshift(mapped); }
      setCollection('wf_templates', templates);
    } else if (eventType === 'DELETE') {
      setCollection('wf_templates', templates.filter((t: any) => t.id !== oldRow.id));
    }
    _notifyKey('wf_templates');
  }

  if (table === 'notifications') {
    const notifications = getCollection<any>('wf_notifications');
    if (eventType === 'INSERT') {
      const mapped = mapNotification(newRow);
      if (!notifications.find((n: any) => n.id === mapped.id)) {
        notifications.unshift(mapped);
        setCollection('wf_notifications', notifications);
      }
    } else if (eventType === 'UPDATE') {
      const mapped = mapNotification(newRow);
      const idx = notifications.findIndex((n: any) => n.id === mapped.id);
      if (idx !== -1) { notifications[idx] = mapped; }
      else { notifications.unshift(mapped); }
      setCollection('wf_notifications', notifications);
    } else if (eventType === 'DELETE') {
      setCollection('wf_notifications', notifications.filter((n: any) => n.id !== oldRow.id));
    }
    _notifyKey('wf_notifications');
  }
}

/**
 * Load older messages for a conversation (pagination).
 * Returns the number of messages loaded (0 = no more).
 */
export async function loadOlderMessages(conversationId: string, batchSize = 50): Promise<number> {
  const wsId = getCurrentWorkspaceId();
  if (!wsId || wsId === 'default') return 0;

  const key = `wf_messages_${conversationId}`;
  const existing = getCollection<Message>(key);

  // If empty cache, fetch latest batch from DB (initial load fallback)
  if (existing.length === 0) {
    const { data: rows } = await supabase
      .from('messages')
      .select('*')
      .eq('workspace_id', wsId)
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(batchSize);
    if (!rows || rows.length === 0) return 0;
    rows.reverse();
    const messages = rows.map(mapMessage);
    setCollection(key, messages);
    _notifyKey(key);
    return messages.length;
  }

  // Find the oldest message's created_at
  const oldestMsg = existing.reduce((oldest, m) =>
    new Date(m.createdAt).getTime() < new Date(oldest.createdAt).getTime() ? m : oldest
  , existing[0]);

  const { data: olderRows } = await supabase
    .from('messages')
    .select('*')
    .eq('workspace_id', wsId)
    .eq('conversation_id', conversationId)
    .lt('created_at', oldestMsg.createdAt)
    .order('created_at', { ascending: false })
    .limit(batchSize);

  if (!olderRows || olderRows.length === 0) return 0;

  olderRows.reverse();
  const olderMessages = olderRows.map(mapMessage);

  // Dedup
  const existingIds = new Set(existing.map(m => m.id));
  const newMsgs = olderMessages.filter(m => !existingIds.has(m.id));

  if (newMsgs.length === 0) return 0;

  // Prepend older messages
  setCollection(key, [...newMsgs, ...existing]);
  _notifyKey(key);
  return newMsgs.length;
}

export function isSynced(): boolean {
  return _synced;
}
